import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LabelInput, LabelError } from 'shared/components/Labels';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import AlertModal from 'shared/components/AlertModal';
import MainSpinner from 'shared/components/MainSpinner';

import useApi from 'hooks/useApi';
import { closeAlert, showAlert } from 'redux/actions/alertActions';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';
import { convertErrorsArrayToString } from 'shared/helpers/helper';

const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { idApp } = useSelector((state) => state.auth);
  const { openAlert, alertType, alertMessage } = useSelector((state) => state.alertState);
  const { customAreaApi } = useApi();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [{ loading }, forgotPassword] = customAreaApi(
    {
      url: `${window.API_V1}/login/remember`,
      method: 'POST',
    },
    { manual: true }
  );

  const onCloseAlert = () => dispatch(closeAlert());
  const onSubmit = async (values) => {
    try {
      const { email } = values;
      const {
        data: { errors: forgotPasswordErrors },
      } = await forgotPassword({
        data: {
          idApp,
          login: email,
          domain: window.location.origin,
        },
      });
      if (forgotPasswordErrors.length) {
        throw new Error(convertErrorsArrayToString(forgotPasswordErrors));
      }
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('forgot_password_success').replace('{email}', email),
        })
      );
    } catch (ex) {
      console.error('error calling forgot password endpoint: ', ex);
      if (ex.response.data.errors[0]?.code === 'CCEL-01') {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('forgot_password_error_reset_password'),
          })
        );
      } else {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('general_error_message'),
          })
        );
      }
    }
  };

  return (
    <form className="form forgot-password__form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="form__form-group">
        <LabelInput
          className="form__form-group-label"
          label={t('general_email')}
          error={errors.email !== undefined}
        />
        <Controller
          name="email"
          control={control}
          defaultValue=""
          rules={{
            required: true,
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('forgot_password_invalid_email'),
            },
          }}
          render={({ field }) => (
            <TextField
              type="email"
              borderRadius={2}
              value={field.value}
              onChange={field.onChange}
              error={errors.email !== undefined}
            />
          )}
        />
        {errors.email?.type === 'required' && (
          <LabelError label={t('forgot_password_required_email')} />
        )}
        {errors.email?.type !== 'required' && <LabelError label={errors?.email?.message ?? ''} />}
      </div>
      <Button
        className="forgot-password__button"
        type="submit"
        label={t('forgot_password_send_email')}
        color="primary"
      />
      {loading && <MainSpinner />}
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={onCloseAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={onCloseAlert}
      />
    </form>
  );
};

export default ForgotPasswordForm;
